import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QuizBloqueio from './components/QuizBloqueio';
import OfertaPlanos from './components/OfertaPlanos';
import ExitIntentLinks from './components/ExitIntentLinks';
import { supabase } from '../../lib/supabaseClient';
import SEOHead from '../../components/feature/SEOHead';

interface LinkItem {
  titulo: string;
  subtitulo?: string;
  path: string;
  icone: string;
  destaque?: boolean;
  badge?: string;
}

interface ExercicioItem {
  titulo: string;
  path: string;
  icone: string;
  categoria: string;
  corCategoria: string;
  popular?: boolean;
  praticantes?: string;
}

interface ExercicioDestaque {
  titulo: string;
  subtitulo: string;
  path: string;
  icone: string;
  tempo: string;
  nivel: string;
  praticantes: string;
  badge?: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const linksSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    name: 'Links | Bia Holmes — PNL Clínica e Alta Performance',
    url: `${SITE_URL}/links`,
    inLanguage: 'pt-BR',
    description: 'Página de links da Bia Holmes. Exercícios gratuitos de PNL, diagnóstico de padrão mental, método completo e redes sociais.',
    mainEntity: {
      '@type': 'Person',
      name: 'Bia Holmes',
      url: `${SITE_URL}/sobre`,
      jobTitle: 'Especialista em PNL Clínica e Alta Performance',
      description: 'Reprogramação mental para executivas que querem performance máxima sem abrir mão de quem são.',
      sameAs: [
        'https://instagram.com/Bia.Holmes',
        'https://youtube.com/@beatrizholmes8581',
        'https://linkedin.com/in/beatrizhmonteirom',
        'https://tiktok.com/@Bia.Holmes',
      ],
    },
  },
];

const exerciciosDestaque: ExercicioDestaque[] = [
  {
    titulo: 'Ancoragem Emocional',
    subtitulo: 'Acesse calma e confiança sob demanda — em segundos.',
    path: '/exercicio-gratis/ancoragem-emocional',
    icone: 'ri-anchor-line',
    tempo: '15 min',
    nivel: 'Iniciante',
    praticantes: '5.129',
    badge: 'MAIS POPULAR',
  },
  {
    titulo: 'Técnica Swish — PNL',
    subtitulo: 'Substitua padrões limitantes por comportamentos de alta performance.',
    path: '/exercicio-gratis/tecnica-swish-pnl',
    icone: 'ri-flashlight-line',
    tempo: '10 min',
    nivel: 'Iniciante',
    praticantes: '3.847',
    badge: 'CLÁSSICO PNL',
  },
  {
    titulo: 'Propósito de Vida',
    subtitulo: 'Conecte-se com sua missão única e viva com clareza total.',
    path: '/exercicio-gratis/proposito-vida',
    icone: 'ri-compass-3-line',
    tempo: '35 min',
    nivel: 'Iniciante',
    praticantes: '4.567',
  },
];

const linksPrincipais: LinkItem[] = [
  {
    titulo: 'Diagnóstico de Padrão Mental',
    subtitulo: 'Descubra qual padrão limita seu próximo nível',
    path: '/diagnostico',
    icone: 'ri-brain-line',
    destaque: true,
    badge: 'GRÁTIS',
  },
  {
    titulo: 'Conheça o Método',
    subtitulo: 'PNL Clínica + Neurociência para executivas',
    path: '/sobre',
    icone: 'ri-compass-3-line',
  },
];

const todosExercicios: ExercicioItem[] = [
  { titulo: 'Ancoragem Emocional', path: '/exercicio-gratis/ancoragem-emocional', icone: 'ri-anchor-line', categoria: 'PNL Clínica', corCategoria: 'gold', popular: true, praticantes: '5.1k' },
  { titulo: 'Propósito de Vida', path: '/exercicio-gratis/proposito-vida', icone: 'ri-compass-3-line', categoria: 'Autoconhecimento', corCategoria: 'gold', popular: true, praticantes: '4.5k' },
  { titulo: 'Técnica Swish', path: '/exercicio-gratis/tecnica-swish-pnl', icone: 'ri-flashlight-line', categoria: 'PNL Clínica', corCategoria: 'gold', popular: true, praticantes: '3.8k' },
  { titulo: 'Linha do Tempo — PNL', path: '/exercicio-gratis/linha-tempo-pnl', icone: 'ri-history-line', categoria: 'Cura Emocional', corCategoria: 'gold', praticantes: '4.2k' },
  { titulo: 'Ressignificação (Reframing)', path: '/exercicio-gratis/ressignificacao-reframing-pnl', icone: 'ri-refresh-line', categoria: 'Crenças', corCategoria: 'gold', praticantes: '3.5k' },
  { titulo: 'Prosperidade e Carreira', path: '/exercicio-gratis/prosperidade-carreira', icone: 'ri-money-dollar-circle-line', categoria: 'Carreira', corCategoria: 'cream', praticantes: '3.2k' },
  { titulo: 'Identidade de Alta Performance', path: '/exercicio-gratis/identidade-alta-performance', icone: 'ri-shield-star-line', categoria: 'Performance', corCategoria: 'blush', praticantes: '2.8k' },
  { titulo: 'Encantations — Tony Robbins', path: '/exercicio-gratis/encantations-tony-robbins', icone: 'ri-mic-line', categoria: 'Alta Performance', corCategoria: 'blush', praticantes: '2.9k' },
  { titulo: 'Amor Ideal', path: '/exercicio-gratis/amor-ideal', icone: 'ri-heart-3-line', categoria: 'Relacionamentos', corCategoria: 'blush', praticantes: '3.4k' },
  { titulo: 'Saúde e Bem-Estar', path: '/exercicio-gratis/saude-bem-estar', icone: 'ri-heart-pulse-line', categoria: 'Saúde', corCategoria: 'cream', praticantes: '2.7k' },
  { titulo: 'Submodalidades — PNL', path: '/exercicio-gratis/submodalidades-pnl', icone: 'ri-contrast-2-line', categoria: 'PNL Avançada', corCategoria: 'gold', praticantes: '1.8k' },
  { titulo: 'Modelagem de Excelência', path: '/exercicio-gratis/modelagem-excelencia-pnl', icone: 'ri-user-star-line', categoria: 'Sucesso', corCategoria: 'gold', praticantes: '2.1k' },
];

const corMap: Record<string, { bg: string; text: string; border: string; iconBg: string; iconText: string }> = {
  gold: { bg: 'bg-gold-500/10', text: 'text-gold-400', border: 'border-gold-500/20', iconBg: 'bg-gold-500/15', iconText: 'text-gold-400' },
  blush: { bg: 'bg-blush-500/10', text: 'text-blush-300', border: 'border-blush-400/20', iconBg: 'bg-blush-500/15', iconText: 'text-blush-300' },
  cream: { bg: 'bg-cream-200/10', text: 'text-cream-300', border: 'border-cream-300/20', iconBg: 'bg-cream-200/15', iconText: 'text-cream-400' },
};

function AnimatedCard({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return (
    <div className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${className}`}>
      {children}
    </div>
  );
}

export default function LinksPage() {
  const [verTodos, setVerTodos] = useState(false);
  const exerciciosVisiveis = verTodos ? todosExercicios : todosExercicios.slice(0, 6);

  useEffect(() => {
    supabase.from('link_bio_analytics').insert({ tipo: 'view' }).then(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-luxury-black relative overflow-x-hidden">
      <SEOHead
        title="Links | Bia Holmes — PNL Clínica e Alta Performance"
        description="12 exercícios gratuitos de PNL, diagnóstico de padrão mental e método completo de reprogramação mental para executivas."
        keywords="Bia Holmes links, PNL grátis, exercícios PNL, diagnóstico mental, reprogramação mental"
        canonical="/links"
        ogType="website"
        schema={linksSchema}
      />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(201,164,64,0.06)_0%,_transparent_60%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-luxury-champagne/30 to-transparent" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center px-4 py-10 pb-16">
        <div className="w-full max-w-[420px]">

          {/* ── PERFIL ─── */}
          <AnimatedCard delay={0} className="flex flex-col items-center text-center mb-8">
            <div className="relative mb-4">
              <div className="w-24 h-24 rounded-full ring-2 ring-gold-500/50 ring-offset-2 ring-offset-luxury-black overflow-hidden">
                <img
                  src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
                  alt="Bia Holmes"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gold-500 rounded-full flex items-center justify-center border-2 border-luxury-black">
                <i className="ri-verified-badge-fill text-luxury-black text-xs" />
              </div>
            </div>
            <h1 className="font-display text-2xl font-semibold text-white tracking-wide mb-1">Bia Holmes</h1>
            <p className="text-gold-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
              PNL Clínica · Alta Performance
            </p>
            <p className="text-white/60 text-sm leading-relaxed max-w-[320px]">
              Reprogramação mental para executivas que querem performance máxima sem abrir mão de quem são.
            </p>
            <div className="flex items-center gap-3 mt-5">
              {[
                { icone: 'ri-instagram-line', href: 'https://instagram.com/Bia.Holmes', label: 'Instagram' },
                { icone: 'ri-youtube-line', href: 'https://youtube.com/@beatrizholmes8581', label: 'YouTube' },
                { icone: 'ri-linkedin-box-fill', href: 'https://linkedin.com/in/beatrizhmonteirom', label: 'LinkedIn' },
                { icone: 'ri-tiktok-line', href: 'https://tiktok.com/@Bia.Holmes', label: 'TikTok' },
              ].map((social) => (
                <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer nofollow" aria-label={social.label}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/50 hover:text-gold-400 hover:border-gold-500/40 hover:bg-gold-500/10 transition-all cursor-pointer">
                  <i className={`${social.icone} text-base`} />
                </a>
              ))}
            </div>
          </AnimatedCard>

          {/* ── QUIZ BLOQUEIO ─── */}
          <AnimatedCard delay={120}>
            <div className="mb-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-500/20" />
                <span className="text-[10px] text-gold-500/50 font-semibold tracking-[0.22em] uppercase">Para você</span>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-500/20" />
              </div>
              <QuizBloqueio />
            </div>
          </AnimatedCard>

          {/* ── LINKS PRINCIPAIS ─── */}
          <AnimatedCard delay={200}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
              <span className="text-[10px] text-gold-500/60 font-semibold tracking-[0.25em] uppercase">Explorar</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
            </div>
          </AnimatedCard>

          <div className="space-y-3 mb-8">
            {linksPrincipais.map((item, i) => (
              <AnimatedCard key={item.path} delay={230 + i * 60}>
                <Link
                  to={item.path}
                  className={`group flex items-center gap-4 w-full px-5 py-4 rounded-2xl border transition-all duration-300 cursor-pointer ${
                    item.destaque
                      ? 'bg-gradient-to-r from-gold-600/20 to-gold-500/10 border-gold-500/40 hover:from-gold-600/30 hover:to-gold-500/20 hover:border-gold-400/60'
                      : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-gold-500/25'
                  }`}
                >
                  {/* Ícone */}
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${
                    item.destaque
                      ? 'bg-gold-500/20 border border-gold-500/30'
                      : 'bg-white/5 border border-white/10 group-hover:bg-gold-500/10 group-hover:border-gold-500/20'
                  } transition-all`}>
                    <i className={`${item.icone} text-xl ${item.destaque ? 'text-gold-400' : 'text-white/50 group-hover:text-gold-400'} transition-colors`} />
                  </div>

                  {/* Texto */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold text-sm ${item.destaque ? 'text-gold-300' : 'text-white/90'} leading-tight`}>
                        {item.titulo}
                      </span>
                      {item.badge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-gold-500/20 text-gold-400 border border-gold-500/30 rounded-full tracking-wider whitespace-nowrap">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    {item.subtitulo && (
                      <p className="text-xs text-white/40 mt-0.5 leading-snug line-clamp-1 group-hover:text-white/55 transition-colors">
                        {item.subtitulo}
                      </p>
                    )}
                  </div>

                  {/* Seta */}
                  <div className="w-7 h-7 flex items-center justify-center shrink-0">
                    <i className={`ri-arrow-right-s-line text-lg transition-all group-hover:translate-x-0.5 ${item.destaque ? 'text-gold-400/70' : 'text-white/25 group-hover:text-gold-400/60'}`} />
                  </div>
                </Link>
              </AnimatedCard>
            ))}
          </div>

          {/* ── EXERCÍCIOS EM DESTAQUE ─── */}
          <AnimatedCard delay={420}>
            <div className="flex items-center gap-3 mb-1">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-500/25" />
              <span className="text-[10px] text-gold-400/70 font-bold tracking-[0.22em] uppercase">Exercícios Gratuitos</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-500/25" />
            </div>
            <div className="text-center mb-5 mt-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] text-white/30 font-medium">
                <i className="ri-checkbox-circle-fill text-gold-500/60 text-xs" />
                12 técnicas · sem cadastro · acesso imediato
              </span>
            </div>
          </AnimatedCard>

          {/* Cards de destaque — top 3 */}
          <div className="space-y-3 mb-4">
            {exerciciosDestaque.map((ex, i) => (
              <AnimatedCard key={ex.path} delay={460 + i * 70}>
                <Link
                  to={ex.path}
                  className="group relative flex items-start gap-4 w-full px-5 py-4 rounded-2xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.07] to-transparent hover:from-gold-500/[0.12] hover:border-gold-400/40 transition-all duration-300 cursor-pointer overflow-hidden"
                >
                  {/* Subtle glow line top */}
                  <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent" />

                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gold-500/15 border border-gold-500/25 shrink-0 group-hover:bg-gold-500/25 transition-all">
                    <i className={`${ex.icone} text-xl text-gold-400`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-bold text-white/90 group-hover:text-white transition-colors leading-tight">
                        {ex.titulo}
                      </span>
                      {ex.badge && (
                        <span className="text-[9px] font-bold px-2 py-0.5 bg-gold-500/25 text-gold-400 border border-gold-500/30 rounded-full tracking-wider whitespace-nowrap">
                          {ex.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/40 leading-snug mb-2 group-hover:text-white/55 transition-colors">
                      {ex.subtitulo}
                    </p>
                    <div className="flex items-center gap-3 text-[10px] text-white/25">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line text-gold-500/60" />
                        {ex.tempo}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-group-line text-gold-500/60" />
                        {ex.praticantes} praticantes
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-seedling-line text-gold-500/60" />
                        {ex.nivel}
                      </span>
                    </div>
                  </div>

                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold-500/10 border border-gold-500/20 shrink-0 group-hover:bg-gold-500/20 transition-all self-center">
                    <i className="ri-play-mini-fill text-gold-400 text-sm group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              </AnimatedCard>
            ))}
          </div>

          {/* ── TODOS OS 12 EXERCÍCIOS ─── */}
          <AnimatedCard delay={640}>
            <div className="mb-2">
              <button
                onClick={() => setVerTodos(!verTodos)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-gold-500/20 transition-all cursor-pointer mb-3 group"
              >
                <div className="flex items-center gap-2">
                  <i className="ri-list-check-2 text-gold-500/60 text-sm" />
                  <span className="text-xs font-semibold text-white/50 group-hover:text-white/70 transition-colors">
                    {verTodos ? 'Recolher lista' : 'Ver todos os 12 exercícios'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-gold-400/70 bg-gold-500/10 border border-gold-500/15 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    12 grátis
                  </span>
                  <i className={`${verTodos ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-white/25 text-sm transition-all`} />
                </div>
              </button>
            </div>
          </AnimatedCard>

          {verTodos && (
            <div className="space-y-1.5 mb-5">
              {todosExercicios.map((ex, i) => {
                const cores = corMap[ex.corCategoria] || corMap.gold;
                return (
                  <AnimatedCard key={ex.path} delay={i * 30}>
                    <Link
                      to={ex.path}
                      className={`group flex items-center gap-3 w-full px-4 py-3 rounded-xl border ${cores.border} bg-white/[0.02] hover:bg-white/[0.05] transition-all cursor-pointer`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${cores.iconBg} border ${cores.border}`}>
                        <i className={`${ex.icone} text-sm ${cores.iconText}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-medium text-white/80 group-hover:text-white transition-colors leading-tight truncate">
                            {ex.titulo}
                          </p>
                          {ex.popular && (
                            <span className="text-[9px] font-bold text-gold-400/70 bg-gold-500/10 border border-gold-500/15 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                              popular
                            </span>
                          )}
                        </div>
                        <p className={`text-[10px] font-semibold ${cores.text} opacity-60 mt-0.5 tracking-wide`}>
                          {ex.categoria} · {ex.praticantes}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line text-sm text-white/20 group-hover:text-white/40 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  </AnimatedCard>
                );
              })}
            </div>
          )}

          {/* CTA para página completa de exercícios */}
          <AnimatedCard delay={680}>
            <Link
              to="/exercicios-gratis"
              className="group flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl border border-gold-500/30 bg-gold-500/8 hover:bg-gold-500/15 hover:border-gold-400/50 transition-all cursor-pointer mb-8"
            >
              <i className="ri-sparkling-2-line text-gold-400 text-base" />
              <span className="text-sm font-bold text-gold-300 tracking-wide group-hover:text-gold-200 transition-colors">
                Explorar todos os exercícios
              </span>
              <i className="ri-arrow-right-line text-gold-400/70 text-sm group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </AnimatedCard>

          {/* ── OFERTA COM CONTADOR ─── */}
          <AnimatedCard delay={710}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
              <span className="text-[10px] text-gold-500/60 font-semibold tracking-[0.25em] uppercase">Acesso Completo</span>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
            </div>
            <div className="mb-8">
              <OfertaPlanos />
            </div>
          </AnimatedCard>

          {/* ── DEPOIMENTO ─── */}
          <AnimatedCard delay={750}>
            <div className="bg-white/[0.03] border border-gold-500/15 rounded-2xl p-5 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-double-quotes-l text-2xl text-gold-500/40" />
                </div>
                <div>
                  <p className="text-white/60 text-sm leading-relaxed italic mb-3">
                    &ldquo;Em 30 dias transformei minha relação com o trabalho, os relacionamentos e comigo mesma. É a ferramenta mais poderosa que já encontrei.&rdquo;
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full overflow-hidden bg-gold-500/20 flex items-center justify-center">
                      <i className="ri-user-line text-gold-400 text-sm" />
                    </div>
                    <div>
                      <p className="text-white/70 text-xs font-semibold">Carolina M.</p>
                      <p className="text-white/30 text-[10px]">Diretora Executiva · São Paulo</p>
                    </div>
                    <div className="ml-auto flex gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className="ri-star-fill text-gold-400 text-xs" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedCard>

          {/* ── CTA FINAL ─── */}
          <AnimatedCard delay={800}>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/25 rounded-full mb-4">
                <i className="ri-user-heart-line text-gold-400 text-xs" />
                <span className="text-gold-400 text-[10px] font-bold tracking-[0.18em] uppercase">+4.200 executivas transformadas</span>
              </div>
              <p className="text-white/40 text-xs leading-relaxed mb-4">
                Reprogramação mental baseada em neurociência e PNL Clínica.<br />Resultados perceptíveis em 21 a 30 dias.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs text-white/25 hover:text-white/50 transition-colors"
              >
                <i className="ri-external-link-line text-xs" />
                biaholmes.com
              </Link>
            </div>
          </AnimatedCard>

          {/* ── FOOTER ─── */}
          <AnimatedCard delay={900}>
            <div className="border-t border-white/[0.06] pt-6 text-center">
              <p className="text-white/20 text-[10px] tracking-wide">
                © {new Date().getFullYear()} Bia Holmes · Todos os direitos reservados
              </p>
              <div className="flex items-center justify-center gap-4 mt-3">
                <Link to="/privacidade" className="text-white/20 text-[10px] hover:text-white/40 transition-colors">
                  Privacidade
                </Link>
                <span className="text-white/10">·</span>
                <Link to="/termos" className="text-white/20 text-[10px] hover:text-white/40 transition-colors">
                  Termos
                </Link>
              </div>
            </div>
          </AnimatedCard>

        </div>
      </div>

      <ExitIntentLinks />
    </div>
  );
}
